/**
 * Revision & Configuration Section Component
 * POV-0082: Build Revision & Configuration section UI
 * 
 * Per PRD Section 10.3, displays:
 * - AS9100D Guidance (Clauses 8.1.2, 8.5.6)
 * - Current Revision card with status and effectivity
 * - Revision History table
 * - Actions: Create New, Release (controlled), Mark Obsolete (controlled)
 * 
 * All timestamps displayed in Indian Standard Time (IST - UTC+5:30)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { 
  GitBranch, 
  Plus, 
  CheckCircle, 
  AlertTriangle,
  Clock,
  Loader2,
  Info,
  Lock,
  RefreshCw,
  XCircle,
  Archive,
  Pencil,
  Trash2
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { ViewRelatedHistoryLink } from './HistorySection';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { formatISTDate } from '../../utils/timezone';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { toast } from 'sonner';
import api from '../../services/api';

/**
 * Effectivity type options
 */
const EFFECTIVITY_TYPES = [
  { value: 'none', label: 'None' },
  { value: 'date', label: 'Date-based' },
  { value: 'lot', label: 'Lot-based' },
  { value: 'serial', label: 'Serial-based' },
];

/**
 * Change reference type options
 */
const CHANGE_REF_TYPES = [
  { value: 'none', label: 'None' },
  { value: 'eco', label: 'ECO (Engineering Change Order)' },
  { value: 'ecr', label: 'ECR (Engineering Change Request)' },
  { value: 'other', label: 'Other' },
];

/**
 * Reason codes for controlled actions
 */
const REASON_CODES = [
  { value: 'engineering_approval', label: 'Engineering Approval' },
  { value: 'quality_approval', label: 'Quality Approval' },
  { value: 'customer_request', label: 'Customer Request' },
  { value: 'regulatory_compliance', label: 'Regulatory Compliance' },
  { value: 'data_correction', label: 'Data Correction' },
  { value: 'process_improvement', label: 'Process Improvement' },
  { value: 'obsolescence', label: 'Obsolescence' },
  { value: 'cost_reduction', label: 'Cost Reduction' },
  { value: 'design_change', label: 'Design Change' },
  { value: 'initial_release', label: 'Initial Release' },
  { value: 'other', label: 'Other' },
];

/**
 * Status badge for revisions
 */
const RevisionStatusBadge = ({ status }) => {
  const statusConfig = {
    draft: { 
      label: 'Draft', 
      className: 'bg-amber-100 text-amber-700 border-amber-200',
      icon: Pencil
    },
    released: { 
      label: 'Released', 
      className: 'bg-green-100 text-green-700 border-green-200',
      icon: CheckCircle
    },
    obsolete: { 
      label: 'Obsolete', 
      className: 'bg-slate-100 text-slate-600 border-slate-200',
      icon: Archive
    },
  };

  const config = statusConfig[status] || statusConfig.draft;
  const Icon = config.icon;

  return (
    <span 
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border',
        config.className
      )}
      data-testid={`revision-status-${status}`}
    >
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
};

/**
 * AS9100D Guidance Banner
 */
const AS9100DGuidance = () => (
  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4" data-testid="as9100d-guidance-revision">
    <div className="flex items-start gap-2">
      <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
      <div className="text-xs text-blue-700">
        <p className="font-medium">AS9100D Clauses 8.1.2, 8.5.6</p>
        <p className="mt-1">Control changes to ensure continued conformity to requirements. Released revisions are immutable.</p>
      </div>
    </div>
  </div>
);

/**
 * Section card wrapper
 */
const SectionCard = ({ title, icon: Icon, children, className }) => (
  <div className={cn("bg-slate-50 rounded-lg p-4 border border-slate-100", className)}>
    {title && (
      <h4 className="flex items-center gap-2 text-xs font-semibold text-slate-600 uppercase tracking-wider mb-3">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {title}
      </h4>
    )}
    {children}
  </div>
);

/**
 * Format date for display (IST)
 */
const formatDate = (dateString) => {
  if (!dateString) return '—';
  return formatISTDate(dateString);
};

/**
 * Format effectivity display
 */
const formatEffectivity = (type, from, to) => {
  if (type === 'none' || !type) return 'None';
  
  const typeLabel = type.charAt(0).toUpperCase() + type.slice(1);
  if (from && to) {
    return `${typeLabel}: ${formatDate(from)} - ${formatDate(to)}`;
  }
  if (from) {
    return `${typeLabel}: ${formatDate(from)} onwards`;
  }
  return `${typeLabel}-based`;
};

/**
 * Create Revision Dialog
 */
const CreateRevisionDialog = ({ 
  open, 
  onOpenChange, 
  onSubmit, 
  nextCode,
  isSubmitting 
}) => {
  const getInitialFormData = useCallback(() => ({
    revision_code: nextCode || 'A',
    effectivity_type: 'none',
    effectivity_from: '',
    effectivity_to: '',
    change_reference_type: 'none',
    change_reference_id: '',
    notes: '',
  }), [nextCode]);

  const [formData, setFormData] = useState(getInitialFormData);

  const handleSubmit = () => {
    if (!formData.revision_code.trim()) {
      toast.error('Revision code is required');
      return;
    }
    onSubmit(formData);
  };

  const handleOpenChange = (isOpen) => {
    if (!isOpen) {
      setFormData(getInitialFormData());
    } else if (isOpen && nextCode && formData.revision_code !== nextCode) {
      // Update revision code when dialog opens with new nextCode
      setFormData(prev => ({ ...prev, revision_code: nextCode }));
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="create-revision-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Create New Revision
          </DialogTitle>
          <DialogDescription>
            Create a new draft revision. It can be edited until released.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="revision-code">Revision Code *</Label>
              <Input
                id="revision-code"
                value={formData.revision_code}
                onChange={(e) => setFormData(prev => ({ ...prev, revision_code: e.target.value.toUpperCase() }))}
                placeholder="e.g., A, B, 01"
                maxLength={10}
                data-testid="revision-code-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="effectivity-type">Effectivity Type</Label>
              <Select 
                value={formData.effectivity_type} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, effectivity_type: v }))}
              >
                <SelectTrigger id="effectivity-type" data-testid="effectivity-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EFFECTIVITY_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {formData.effectivity_type === 'date' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="effectivity-from">Effective From</Label>
                <Input
                  id="effectivity-from"
                  type="date"
                  value={formData.effectivity_from}
                  onChange={(e) => setFormData(prev => ({ ...prev, effectivity_from: e.target.value }))}
                  data-testid="effectivity-from-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="effectivity-to">Effective To (optional)</Label>
                <Input
                  id="effectivity-to"
                  type="date"
                  value={formData.effectivity_to}
                  onChange={(e) => setFormData(prev => ({ ...prev, effectivity_to: e.target.value }))}
                  data-testid="effectivity-to-input"
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="change-ref-type">Change Reference Type</Label>
              <Select 
                value={formData.change_reference_type} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, change_reference_type: v }))}
              >
                <SelectTrigger id="change-ref-type" data-testid="change-ref-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CHANGE_REF_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {formData.change_reference_type !== 'none' && (
              <div className="space-y-2">
                <Label htmlFor="change-ref-id">Reference ID</Label>
                <Input
                  id="change-ref-id"
                  value={formData.change_reference_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, change_reference_id: e.target.value }))}
                  placeholder="e.g., ECO-2026-001"
                  data-testid="change-ref-id-input"
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Add revision notes..."
              rows={3}
              data-testid="revision-notes-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting} data-testid="create-revision-submit">
            {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Create Draft
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Controlled Action Dialog (Release / Mark Obsolete)
 */
const ControlledActionDialog = ({ 
  open, 
  onOpenChange, 
  onConfirm,
  action,
  revisionCode,
  isSubmitting 
}) => {
  const [reasonCode, setReasonCode] = useState('');
  const [comment, setComment] = useState('');

  const isRelease = action === 'release';
  const title = isRelease ? 'Release Revision' : 'Mark Revision Obsolete';
  const description = isRelease 
    ? `You are about to release Rev ${revisionCode}. Once released, this revision becomes immutable.`
    : `You are about to mark Rev ${revisionCode} as obsolete. This action cannot be undone.`;

  const handleConfirm = () => {
    if (!reasonCode) {
      toast.error('Please select a reason code');
      return;
    }
    if (!comment.trim() || comment.trim().length < 10) {
      toast.error('Please provide a comment (at least 10 characters)');
      return;
    }
    onConfirm(reasonCode, comment);
  };

  const handleOpenChange = (open) => {
    if (!open) {
      setReasonCode('');
      setComment('');
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid={`${action}-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-amber-600" />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs text-amber-700 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              This is a controlled action requiring justification.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="action-reason-code">Reason Code *</Label>
            <Select value={reasonCode} onValueChange={setReasonCode}>
              <SelectTrigger id="action-reason-code" data-testid="action-reason-select">
                <SelectValue placeholder="Select a reason..." />
              </SelectTrigger>
              <SelectContent>
                {REASON_CODES.map((reason) => (
                  <SelectItem key={reason.value} value={reason.value}>
                    {reason.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="action-comment">Comment * (min 10 chars)</Label>
            <Textarea
              id="action-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Provide justification for this action..."
              rows={3}
              data-testid="action-comment-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirm} 
            disabled={isSubmitting}
            variant={isRelease ? 'default' : 'destructive'}
            data-testid="action-confirm-btn"
          >
            {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isRelease ? 'Release Revision' : 'Mark Obsolete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Edit Draft Revision Dialog
 */
const EditRevisionDialog = ({ 
  open, 
  onOpenChange, 
  revision,
  onSubmit, 
  isSubmitting 
}) => {
  const getInitialFormData = useCallback(() => {
    if (revision) {
      return {
        revision_code: revision.revision_code || '',
        effectivity_type: revision.effectivity_type || 'none',
        effectivity_from: revision.effectivity_from ? revision.effectivity_from.split('T')[0] : '',
        effectivity_to: revision.effectivity_to ? revision.effectivity_to.split('T')[0] : '',
        change_reference_type: revision.change_reference_type || 'none',
        change_reference_id: revision.change_reference_id || '',
        notes: revision.notes || '',
      };
    }
    return {
      revision_code: '',
      effectivity_type: 'none',
      effectivity_from: '',
      effectivity_to: '',
      change_reference_type: 'none',
      change_reference_id: '',
      notes: '',
    };
  }, [revision]);

  const [formData, setFormData] = useState(getInitialFormData);

  // Reset form when revision changes and dialog is open
  const handleOpenChange = (isOpen) => {
    if (isOpen && revision) {
      setFormData(getInitialFormData());
    }
    onOpenChange(isOpen);
  };

  const handleSubmit = () => {
    if (!formData.revision_code.trim()) {
      toast.error('Revision code is required');
      return;
    }
    onSubmit(formData);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="edit-revision-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="w-4 h-4" />
            Edit Draft Revision
          </DialogTitle>
          <DialogDescription>
            Modify the draft revision. Changes are saved immediately.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-revision-code">Revision Code *</Label>
              <Input
                id="edit-revision-code"
                value={formData.revision_code}
                onChange={(e) => setFormData(prev => ({ ...prev, revision_code: e.target.value.toUpperCase() }))}
                placeholder="e.g., A, B, 01"
                maxLength={10}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-effectivity-type">Effectivity Type</Label>
              <Select 
                value={formData.effectivity_type} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, effectivity_type: v }))}
              >
                <SelectTrigger id="edit-effectivity-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EFFECTIVITY_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {formData.effectivity_type === 'date' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-effectivity-from">Effective From</Label>
                <Input
                  id="edit-effectivity-from"
                  type="date"
                  value={formData.effectivity_from}
                  onChange={(e) => setFormData(prev => ({ ...prev, effectivity_from: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-effectivity-to">Effective To (optional)</Label>
                <Input
                  id="edit-effectivity-to"
                  type="date"
                  value={formData.effectivity_to}
                  onChange={(e) => setFormData(prev => ({ ...prev, effectivity_to: e.target.value }))}
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-change-ref-type">Change Reference Type</Label>
              <Select 
                value={formData.change_reference_type} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, change_reference_type: v }))}
              >
                <SelectTrigger id="edit-change-ref-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CHANGE_REF_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {formData.change_reference_type !== 'none' && (
              <div className="space-y-2">
                <Label htmlFor="edit-change-ref-id">Reference ID</Label>
                <Input
                  id="edit-change-ref-id"
                  value={formData.change_reference_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, change_reference_id: e.target.value }))}
                  placeholder="e.g., ECO-2026-001"
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-notes">Notes (optional)</Label>
            <Textarea
              id="edit-notes"
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Add revision notes..."
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Delete Confirmation Dialog
 */
const DeleteRevisionDialog = ({ 
  open, 
  onOpenChange, 
  onConfirm,
  revisionCode,
  isSubmitting 
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="delete-revision-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <Trash2 className="w-4 h-4" />
            Delete Draft Revision
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to delete Rev {revisionCode}? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            variant="destructive" 
            onClick={onConfirm} 
            disabled={isSubmitting}
            data-testid="delete-confirm-btn"
          >
            {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Delete Revision
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Current Revision Card
 */
const CurrentRevisionCard = ({ revision, onRelease, onCreateNew, onEdit, onDelete }) => {
  if (!revision) {
    return (
      <SectionCard title="Current Revision" icon={GitBranch}>
        <div className="text-center py-6">
          <p className="text-sm text-slate-500 mb-4">No revisions exist for this part yet.</p>
          <Button onClick={onCreateNew} data-testid="create-first-revision-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create First Revision
          </Button>
        </div>
      </SectionCard>
    );
  }

  const isDraft = revision.revision_status === 'draft';
  const isReleased = revision.revision_status === 'released';

  return (
    <SectionCard className="mb-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="text-lg font-semibold text-slate-900" data-testid="current-revision-code">
            Rev {revision.revision_code}
          </span>
          <RevisionStatusBadge status={revision.revision_status} />
        </div>
        {isDraft && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onEdit} data-testid="edit-draft-btn">
              <Pencil className="w-3 h-3 mr-1" />
              Edit
            </Button>
            <Button variant="outline" size="sm" onClick={onDelete} data-testid="delete-draft-btn">
              <Trash2 className="w-3 h-3 mr-1" />
              Delete
            </Button>
            <Button size="sm" onClick={onRelease} data-testid="release-btn">
              <CheckCircle className="w-3 h-3 mr-1" />
              Release
            </Button>
          </div>
        )}
        {isReleased && (
          <Button onClick={onCreateNew} size="sm" data-testid="create-new-revision-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create New Revision
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <span className="text-slate-500">Effectivity:</span>
          <p className="font-medium" data-testid="revision-effectivity">
            {formatEffectivity(revision.effectivity_type, revision.effectivity_from, revision.effectivity_to)}
          </p>
        </div>
        <div>
          <span className="text-slate-500">Change Ref:</span>
          <p className="font-medium" data-testid="revision-change-ref">
            {revision.change_reference_id || '—'}
          </p>
        </div>
        {revision.released_at && (
          <div className="col-span-2">
            <span className="text-slate-500">Released:</span>
            <p className="font-medium" data-testid="revision-released">
              {formatDate(revision.released_at)} by {revision.released_by}
            </p>
          </div>
        )}
        {revision.notes && (
          <div className="col-span-2">
            <span className="text-slate-500">Notes:</span>
            <p className="font-medium text-slate-700" data-testid="revision-notes">
              {revision.notes}
            </p>
          </div>
        )}
      </div>
    </SectionCard>
  );
};

/**
 * Revision History Table
 */
const RevisionHistoryTable = ({ revisions, onMarkObsolete }) => {
  if (!revisions || revisions.length === 0) {
    return null;
  }

  return (
    <div className="mt-4">
      <h4 className="flex items-center gap-2 text-xs font-semibold text-slate-600 uppercase tracking-wider mb-3">
        <Clock className="w-3.5 h-3.5" />
        Revision History
      </h4>
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-xs">Rev</TableHead>
              <TableHead className="text-xs">Status</TableHead>
              <TableHead className="text-xs">Effectivity</TableHead>
              <TableHead className="text-xs">Change Ref</TableHead>
              <TableHead className="text-xs">Released</TableHead>
              <TableHead className="text-xs text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {revisions.map((rev) => (
              <TableRow key={rev.part_revision_id} data-testid={`revision-row-${rev.revision_code}`}>
                <TableCell className="font-medium">{rev.revision_code}</TableCell>
                <TableCell>
                  <RevisionStatusBadge status={rev.revision_status} />
                </TableCell>
                <TableCell className="text-xs text-slate-600">
                  {formatEffectivity(rev.effectivity_type, rev.effectivity_from, rev.effectivity_to)}
                </TableCell>
                <TableCell className="text-xs text-slate-600">
                  {rev.change_reference_id || '—'}
                </TableCell>
                <TableCell className="text-xs text-slate-600">
                  {rev.released_at ? formatDate(rev.released_at) : '—'}
                </TableCell>
                <TableCell className="text-right">
                  {rev.revision_status === 'released' && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => onMarkObsolete(rev)}
                      data-testid={`obsolete-btn-${rev.revision_code}`}
                    >
                      <Archive className="w-3 h-3 mr-1" />
                      <span className="text-xs">Mark Obsolete</span>
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

/**
 * Main Revision Section Component
 */
const RevisionSection = ({ partId, onViewRelatedHistory }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [revisions, setRevisions] = useState([]);
  const [currentRevision, setCurrentRevision] = useState(null);
  const [nextCode, setNextCode] = useState('A');
  const [error, setError] = useState(null);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showReleaseDialog, setShowReleaseDialog] = useState(false);
  const [showObsoleteDialog, setShowObsoleteDialog] = useState(false);
  const [selectedRevision, setSelectedRevision] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load revisions
  const loadRevisions = useCallback(async () => {
    if (!partId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const [revisionsRes, nextCodeRes] = await Promise.all([
        api.get(`/api/v1/revisions/part/${partId}`),
        api.get(`/api/v1/revisions/part/${partId}/next-code`),
      ]);
      
      const allRevisions = revisionsRes.data.revisions || [];
      setRevisions(allRevisions);
      setNextCode(nextCodeRes.data.next_code || 'A');
      
      // Find current revision (first draft, or most recently released)
      const draft = allRevisions.find(r => r.revision_status === 'draft');
      const released = allRevisions.find(r => r.revision_status === 'released');
      setCurrentRevision(draft || released || null);
    } catch (err) {
      console.error('Failed to load revisions:', err);
      setError(err.response?.data?.detail || 'Failed to load revisions');
    } finally {
      setIsLoading(false);
    }
  }, [partId]);

  useEffect(() => {
    loadRevisions();
  }, [loadRevisions]);

  // Create revision
  const handleCreateRevision = async (formData) => {
    setIsSubmitting(true);
    try {
      await api.post('/api/v1/revisions/', {
        part_id: partId,
        ...formData,
        effectivity_from: formData.effectivity_from || null,
        effectivity_to: formData.effectivity_to || null,
      });
      toast.success(`Draft revision ${formData.revision_code} created`);
      setShowCreateDialog(false);
      loadRevisions();
    } catch (err) {
      console.error('Failed to create revision:', err);
      toast.error(err.response?.data?.detail || 'Failed to create revision');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Edit revision
  const handleEditRevision = async (formData) => {
    if (!selectedRevision) return;
    
    setIsSubmitting(true);
    try {
      await api.put(`/api/v1/revisions/${selectedRevision.part_revision_id}`, {
        ...formData,
        effectivity_from: formData.effectivity_from || null,
        effectivity_to: formData.effectivity_to || null,
      });
      toast.success('Revision updated');
      setShowEditDialog(false);
      setSelectedRevision(null);
      loadRevisions();
    } catch (err) {
      console.error('Failed to update revision:', err);
      toast.error(err.response?.data?.detail || 'Failed to update revision');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete revision
  const handleDeleteRevision = async () => {
    if (!selectedRevision) return;
    
    setIsSubmitting(true);
    try {
      await api.delete(`/api/v1/revisions/${selectedRevision.part_revision_id}`);
      toast.success(`Draft revision ${selectedRevision.revision_code} deleted`);
      setShowDeleteDialog(false);
      setSelectedRevision(null);
      loadRevisions();
    } catch (err) {
      console.error('Failed to delete revision:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete revision');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Release revision
  const handleReleaseRevision = async (reasonCode, comment) => {
    if (!selectedRevision) return;
    
    setIsSubmitting(true);
    try {
      await api.post(`/api/v1/revisions/${selectedRevision.part_revision_id}/release`, {
        reason_code: reasonCode,
        comment: comment,
        released_by: 'current_user', // Will be replaced by actual user on backend
      });
      toast.success(`Revision ${selectedRevision.revision_code} released`);
      setShowReleaseDialog(false);
      setSelectedRevision(null);
      loadRevisions();
    } catch (err) {
      console.error('Failed to release revision:', err);
      toast.error(err.response?.data?.detail || 'Failed to release revision');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Mark obsolete
  const handleMarkObsolete = async (reasonCode, comment) => {
    if (!selectedRevision) return;
    
    setIsSubmitting(true);
    try {
      await api.post(`/api/v1/revisions/${selectedRevision.part_revision_id}/obsolete`, {
        reason_code: reasonCode,
        comment: comment,
        obsoleted_by: 'current_user', // Will be replaced by actual user on backend
      });
      toast.success(`Revision ${selectedRevision.revision_code} marked as obsolete`);
      setShowObsoleteDialog(false);
      setSelectedRevision(null);
      loadRevisions();
    } catch (err) {
      console.error('Failed to mark obsolete:', err);
      toast.error(err.response?.data?.detail || 'Failed to mark revision obsolete');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle action buttons
  const handleEditClick = () => {
    setSelectedRevision(currentRevision);
    setShowEditDialog(true);
  };

  const handleDeleteClick = () => {
    setSelectedRevision(currentRevision);
    setShowDeleteDialog(true);
  };

  const handleReleaseClick = () => {
    setSelectedRevision(currentRevision);
    setShowReleaseDialog(true);
  };

  const handleObsoleteClick = (revision) => {
    setSelectedRevision(revision);
    setShowObsoleteDialog(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <XCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
        <p className="text-sm text-red-600">{error}</p>
        <Button variant="outline" size="sm" onClick={loadRevisions} className="mt-4">
          <RefreshCw className="w-3 h-3 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="revision-section">
      {/* AS9100D Guidance */}
      <AS9100DGuidance />

      {/* Current Revision Card */}
      <CurrentRevisionCard
        revision={currentRevision}
        onRelease={handleReleaseClick}
        onCreateNew={() => setShowCreateDialog(true)}
        onEdit={handleEditClick}
        onDelete={handleDeleteClick}
      />

      {/* Revision History Table */}
      <RevisionHistoryTable 
        revisions={revisions} 
        onMarkObsolete={handleObsoleteClick}
      />

      {/* View Related History Link */}
      <div className="mt-4">
        <ViewRelatedHistoryLink 
          sectionKey="revision_configuration" 
          onClick={onViewRelatedHistory} 
        />
      </div>

      {/* Dialogs */}
      <CreateRevisionDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSubmit={handleCreateRevision}
        nextCode={nextCode}
        isSubmitting={isSubmitting}
      />

      <EditRevisionDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        revision={selectedRevision}
        onSubmit={handleEditRevision}
        isSubmitting={isSubmitting}
      />

      <DeleteRevisionDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onConfirm={handleDeleteRevision}
        revisionCode={selectedRevision?.revision_code}
        isSubmitting={isSubmitting}
      />

      <ControlledActionDialog
        open={showReleaseDialog}
        onOpenChange={setShowReleaseDialog}
        onConfirm={handleReleaseRevision}
        action="release"
        revisionCode={selectedRevision?.revision_code}
        isSubmitting={isSubmitting}
      />

      <ControlledActionDialog
        open={showObsoleteDialog}
        onOpenChange={setShowObsoleteDialog}
        onConfirm={handleMarkObsolete}
        action="obsolete"
        revisionCode={selectedRevision?.revision_code}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default RevisionSection;
